<script setup lang="ts">
import VoixDrop from '@voix/components/chrome/drag-and-drop/VoixDrop.vue'
import { dragEnd, dragStart } from '@voix/composables/useDraggable'
import { computed, defineProps } from 'vue'
import get from 'lodash/get'
import { $voixFetch, useNuxtApp } from '#imports'

const props = defineProps({
  sortModel: {
    type: String,
    required: true,
  },
  items: {
    type: Array,
    required: true,
  },
  sortLabelColumn: {
    type: String,
    required: true,
  },
  columns: Array<any>,
})

const emit = defineEmits(['refresh'])

const sortLabelColumnData = computed(() => {
  if (!props.columns)
    return []

  return props.columns.filter((column: any) => column.field === props.sortLabelColumn)
})

function submitSortValues(ids: string[]) {
  // This is where you would submit the new sort order to the server
  $voixFetch(
    `/api/admin/voix/sortables`,
    {
      method: 'PUT',
      body: JSON.stringify({ order: ids, model: props.sortModel }),
    },
  ).then(() => {
    emit('refresh')
  })
}

// When an element is dropped at the top of the data array
function dropTopOfDataArray(id: string) {
  const sortArray = JSON.parse(JSON.stringify(props.items))
  const item = sortArray.find((i: { id: string }) => i.id.toString() === id.toString())
  // Move that item to the top of the array
  if (item) {
    const index = sortArray.indexOf(item)
    sortArray.splice(index, 1)
    sortArray.unshift(item)
  }

  // Reduce the array down to an array of ids
  const ids = sortArray.map((i: { id: string }) => i.id)
  submitSortValues(ids)
}

// When an element is dropped in the middle of the data array on any divider that
// isn't that top one. This could be in between a root level slice or inside a slice
// as a child.
function dropMiddleOfDataArray(id: string, destinationId: string) {
  const sortArray = JSON.parse(JSON.stringify(props.items))

  const file = sortArray.find((f: { id: string }) => f.id.toString() === id.toString())
  // Move that file to after the destination id
  if (file) {
    const index = sortArray.indexOf(file)
    sortArray.splice(index, 1)
    const destinationIndex = sortArray.findIndex((f: { id: string }) => f.id.toString() === destinationId.toString())
    sortArray.splice(destinationIndex + 1, 0, file)
  }

  // Reduce the array down to an array of ids
  const ids = sortArray.map((i: { id: string }) => i.id)
  submitSortValues(ids)
}

const { $voix } = useNuxtApp()
function getFieldValue(item: object, field: string, format: (string: any, record: any) => string | undefined) {
  if (field === 'language_code') {
    const language = $voix.options.languages.find((language: any) => language.code === get(item, field))
    if (language)
      return language.icon
  }

  if (format)
    return format(get(item, field), item)
  return get(item, field)
}
</script>

<template>
  <div>
    <VoixDrop
      v-slot="{ item, key }" :entire-data-array="items" :data-array="items" @drop-at-top="dropTopOfDataArray"
      @drop-in-middle="dropMiddleOfDataArray"
    >
      <div class="group flex cursor-pointer hover:voix-admin-bg-lightest duration-200 border-b voix-admin-border-light">
        <div
          class="py-3 px-4 text-xs text-gray-600 group-hover:text-gray-900 font-medium  cursor-grab" draggable="true"
          @dragstart="dragStart($event, `${item.id}`)" @dragend="dragEnd"
        >
          <Icon name="heroicons:bars-3-solid" class="w-4 h-4" />
        </div>
        <div class="py-3 px-4 text-xs text-gray-600 group-hover:text-gray-900 font-medium select-none">
          #{{ key + 1 }}
        </div>
        <div
          v-for="column in sortLabelColumnData" :key="column.field"
          class="py-3 px-4 text-xs text-gray-600 group-hover:text-gray-900 font-medium select-none"
        >
          <div v-html="getFieldValue(item, column.field, column.format)" />
        </div>
      </div>
    </VoixDrop>
  </div>
</template>
